import React, { PureComponent, Component } from "react";
import styled from "styled-components";
import Fragment from "react-dot-fragment";
import { postRequest, patchRequest } from "../../../api/ApiService";
import { incrementUnacceptedDecisions } from "../../../api/SubmitDecisions";
import { logError } from "../../../services/utils";
import EventEmitter from "../../../services/event-emitter";
import { changeLocation } from "../../../api/Location";
import ErrorAlert from "../../alerts/error";
import FinanceStyled from "../../finance/style";

const PRODUCTS_URL = `${process.env.REACT_APP_API_URL}team/products/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";

const RANK_MIN = 1;
const RANK_MAX = 10;
const RANK_DEFAULT = 5;
const NAME_MAX = 6;

const FEATURES = [
  { key: "pixels_rank", label: "Resolution" },
  { key: "sensitivity_rank", label: "Sensitivity" },
  { key: "power_rank", label: "Power" },
  { key: "temp_range_rank", label: "Temperature" },
];

// ---------- styled components ----------

const Page = styled.div`
  padding: 24px 40px 64px;
  max-width: 760px;
  margin: 0 auto;
  font-family: ${FONT};
`;

const HeaderBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TitleIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #DBEAFE;
  color: #2563EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 4px 10px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const FormCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E9F0;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 2px rgba(15, 23, 41, 0.04);
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px solid ${p => (p.invalid ? "#DC2626" : "#E5E9F0")};
  background: #F5F7FB;
  font-family: ${FONT};
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.94);
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    border-color: ${p => (p.invalid ? "#DC2626" : "#3A507B")};
    box-shadow: 0 0 0 3px ${p => (p.invalid
      ? "rgba(220, 38, 38, 0.12)"
      : "rgba(58, 80, 123, 0.12)")};
    background: #FFFFFF;
  }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(0, 27, 59, 0.5);
  margin-top: 6px;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #DC2626;
  font-weight: 600;
  margin-top: 6px;
`;

const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 56px;
  gap: 16px;
  align-items: center;
`;

// Column header row — mirrors FeatureRow's grid so the "Quality Level"
// label sits directly above the rank-badge column.
const FeatureHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 56px;
  gap: 16px;
  align-items: center;
  margin-bottom: 6px;
`;

const FeatureColumnHeader = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
`;

const FeatureName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.94);
`;

const RankBadge = styled.div`
  background: #3A507B;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 800;
  border-radius: 10px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-variant-numeric: tabular-nums;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 4px;
`;

const BtnSecondary = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  color: rgba(0, 27, 59, 0.6);
  padding: 12px 22px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.6);
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled.button`
  background: #3A507B;
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(58, 80, 123, 0.25);

  &:hover:not(:disabled) {
    background: #2E3F62;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Custom div-based slider for product feature ranks (1-10). Mirrors the
// slider used in the marketing pages but stepped to integers.
const SliderRoot = styled.div`
  position: relative;
  height: 24px;
  display: flex;
  align-items: center;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  touch-action: none;
  user-select: none;
  min-width: 0;
`;

const SliderTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 6px;
  background: #E5E9F0;
  border-radius: 999px;
  pointer-events: none;
`;

const SliderFill = styled.div`
  position: absolute;
  left: 0;
  height: 6px;
  background: #3A507B;
  border-radius: 999px;
  pointer-events: none;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: 22px;
  height: 22px;
  background: #FFFFFF;
  border: 3px solid #3A507B;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(58, 80, 123, 0.25);
  transform: translateX(-50%);
  top: 50%;
  margin-top: -11px;
  pointer-events: none;
`;

class FeatureSlider extends Component {
  constructor(props) {
    super(props);
    this.state = { dragging: false };
  }

  componentWillUnmount() {
    this.detach();
  }

  setRef = (el) => { this.rootEl = el; };

  attach() {
    window.addEventListener("mousemove", this.onWindowMove);
    window.addEventListener("mouseup", this.onWindowUp);
    window.addEventListener("touchmove", this.onWindowTouchMove, { passive: false });
    window.addEventListener("touchend", this.onWindowUp);
  }

  detach() {
    window.removeEventListener("mousemove", this.onWindowMove);
    window.removeEventListener("mouseup", this.onWindowUp);
    window.removeEventListener("touchmove", this.onWindowTouchMove);
    window.removeEventListener("touchend", this.onWindowUp);
  }

  updateFromX = (clientX) => {
    if (!this.rootEl || this.props.disabled) return;
    const rect = this.rootEl.getBoundingClientRect();
    if (rect.width <= 0) return;
    const { min, max } = this.props;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let raw = min + (max - min) * ratio;
    raw = Math.round(raw);
    raw = Math.max(min, Math.min(max, raw));
    if (this.props.onChange) this.props.onChange(raw);
  };

  onMouseDown = (e) => {
    if (this.props.disabled) return;
    e.preventDefault();
    this.setState({ dragging: true });
    this.attach();
    this.updateFromX(e.clientX);
  };

  onTouchStart = (e) => {
    if (this.props.disabled || !e.touches || !e.touches.length) return;
    this.setState({ dragging: true });
    this.attach();
    this.updateFromX(e.touches[0].clientX);
  };

  onWindowMove = (e) => {
    if (!this.state.dragging) return;
    this.updateFromX(e.clientX);
  };

  onWindowTouchMove = (e) => {
    if (!this.state.dragging || !e.touches || !e.touches.length) return;
    e.preventDefault();
    this.updateFromX(e.touches[0].clientX);
  };

  onWindowUp = () => {
    if (!this.state.dragging) return;
    this.setState({ dragging: false });
    this.detach();
  };

  render() {
    const { value, min, max, disabled } = this.props;
    const range = max - min;
    const pct = range > 0
      ? Math.max(0, Math.min(100, ((Number(value) - min) / range) * 100))
      : 0;
    return (
      <SliderRoot
        innerRef={this.setRef}
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchStart}
        disabled={disabled}
      >
        <SliderTrack />
        <SliderFill style={{ width: `${pct}%` }} />
        <SliderThumb style={{ left: `${pct}%` }} />
      </SliderRoot>
    );
  }
}

// ---------- component ----------

class AddProductPage extends PureComponent {
  constructor(props) {
    super(props);
    // When `editingProduct` is supplied the popup runs in edit mode:
    // name is locked, sliders seed from the existing product, and the
    // submit handler issues a PATCH instead of a POST.
    const editing = props.editingProduct;
    this.state = {
      name: editing ? (editing.name || "") : "",
      ranks: {
        pixels_rank: editing
          ? Number(editing.pixels_rank) || RANK_DEFAULT
          : RANK_DEFAULT,
        sensitivity_rank: editing
          ? Number(editing.sensitivity_rank) || RANK_DEFAULT
          : RANK_DEFAULT,
        power_rank: editing
          ? Number(editing.power_rank) || RANK_DEFAULT
          : RANK_DEFAULT,
        temp_range_rank: editing
          ? Number(editing.temp_range_rank) || RANK_DEFAULT
          : RANK_DEFAULT,
      },
      isSaving: false,
      errorMessage: null,
      nameInvalid: false,
    };
    this.eventemitter = EventEmitter.getInstance();
  }

  get isEditMode() {
    return !!this.props.editingProduct;
  }

  componentDidMount() {
    this._mounted = true;
  }

  goToCustomerAnalysis = e => {
    e.preventDefault();
    this.eventemitter.trigger("coaching-feedback-hide");
    changeLocation("/ceo/analysis/market");
  };

  showPopupIsThereAMarket = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      "Nice work! You've created a product, but is there a market?",
      "Essentially will a customer buy your product given its features",
      <Fragment>
        <b>I like you so here's a hint</b><br />
        Understand the customer needs/wants first (i.e. review <a href="/ceo/analysis/market" onClick={this.goToCustomerAnalysis}>customer analysis</a>)...you're welcome!
      </Fragment>,
    ]);
  };

  showPopupNotReleasedThisYear = () => {
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      "Heads up! Your product won't be released this year given your product features ranking (i.e. its higher quality).",
      "The higher the design quality, the longer it takes for your product to be released to the market.",
      <Fragment>
        <b>Here's the real dilemma, as with most Product Managers (PMs):</b><br />
        Do you build a higher quality product now and forgo potential revenues this year or create a lower quality product now and upgrade features as you go?
      </Fragment>,
    ]);
  };

  componentWillUnmount() {
    this._mounted = false;
  }

  onChangeName = (e) => {
    // Match the existing add-product modal's filter: alphanumeric only,
    // max 6 chars. Keeps the new flow API-compatible with the legacy
    // create endpoint.
    const cleaned = (e.target.value || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, NAME_MAX);
    this.setState({ name: cleaned, nameInvalid: false });
  };

  onChangeRank = (key) => (value) => {
    this.setState({
      ranks: { ...this.state.ranks, [key]: value },
    });
  };

  hideErrorMessage = () => {
    this.setState({ errorMessage: null });
  };

  // Validates the name against the existing product list (case-insensitive
  // unique) and the 1-6 alphanumeric requirement before issuing a POST.
  validateName = () => {
    const { name } = this.state;
    if (!name || name.length === 0) {
      return "Product name is required.";
    }
    if (name.length > NAME_MAX) {
      return `Product name must be ${NAME_MAX} characters or fewer.`;
    }
    const existingNames = Object.keys(this.props.existingProducts || {})
      .map(uid => {
        const productRounds = Object.values(this.props.existingProducts[uid]);
        return productRounds.length > 0 ? productRounds[0].name : null;
      })
      .filter(Boolean)
      .map(n => n.toLowerCase());
    if (existingNames.includes(name.toLowerCase())) {
      return "A product with that name already exists.";
    }
    return null;
  };

  submit = () => {
    const { name, ranks } = this.state;
    const { editingProduct } = this.props;
    const editing = this.isEditMode;

    // Skip uniqueness/validation in edit mode — name is locked.
    if (!editing) {
      const nameError = this.validateName();
      if (nameError) {
        this.setState({ errorMessage: nameError, nameInvalid: true });
        return;
      }
    }

    this.setState({ isSaving: true });

    // Capture BEFORE the POST: is this Round 1 AND the team's first
    // ever product? Used to fire the "Nice work on creating your first
    // product!" coaching popup once, on the very first successful save.
    const wasFirstProductRd1 =
      !editing
      && Number(this.props.currentRound) === 1
      && Object.keys(this.props.existingProducts || {}).length === 0;

    const payload = {
      pixels_rank: ranks.pixels_rank,
      sensitivity_rank: ranks.sensitivity_rank,
      power_rank: ranks.power_rank,
      temp_range_rank: ranks.temp_range_rank,
    };
    if (!editing) payload.name = name;

    const request = editing
      ? patchRequest(`${PRODUCTS_URL}${editingProduct.id}/`).send(payload)
      : postRequest(PRODUCTS_URL).send(payload);

    request
      .then(res => {
        // Coaching popups (ported from ModalPDDecisionsAddProduct /
        // ModalPDUpdateProduct): the backend flags an aggressive quality
        // build via months_available_to_produce === 0 and a features
        // profile that likely misses the market via
        // minimum_requirement_feedback === 1.
        const body = (res && res.body) || {};
        const minimumRequirementFeedback = Number(body.minimum_requirement_feedback);
        const monthsAvailableToProduce = Number(body.months_available_to_produce);
        if (minimumRequirementFeedback === 1) {
          this.showPopupIsThereAMarket();
        } else if (monthsAvailableToProduce === 0) {
          this.showPopupNotReleasedThisYear();
        }

        incrementUnacceptedDecisions("development");
        if (this.props.reloadProducts) this.props.reloadProducts();
        if (this.props.showSuccessMessage) {
          this.props.showSuccessMessage(
            editing
              ? `Product "${name}" was successfully updated.`
              : `Product "${name}" was successfully created.`
          );
        }
        if (this._mounted) {
          this.setState({ isSaving: false });
        }
        if (this.props.onClose) this.props.onClose();
        if (wasFirstProductRd1 && this.props.onFirstProductCreated) {
          this.props.onFirstProductCreated();
        }
      })
      .catch(err => {
        logError(err, "AddProductPage", "submit");
        if (this._mounted) {
          this.setState({
            isSaving: false,
            errorMessage: editing
              ? "Something went wrong saving these changes. Please try again."
              : "Something went wrong creating your product. Please try again.",
          });
        }
      });
  };

  render() {
    const { onClose } = this.props;
    const { name, ranks, isSaving, errorMessage, nameInvalid } = this.state;
    const editing = this.isEditMode;

    return (
      <FinanceStyled.LenderOverlay>
        {errorMessage && (
          <ErrorAlert
            title="Heads up"
            message={errorMessage}
            onHide={this.hideErrorMessage}
          />
        )}
        <Page>
          <HeaderBlock>
            <HeaderLeft>
              <TitleRow>
                <TitleIcon>🧬</TitleIcon>
                <Title>{editing ? 'Edit Product' : 'Add Product'}</Title>
              </TitleRow>
              <Subtitle>
                {editing
                  ? `Update the quality for each of ${name}'s 4 features.`
                  : 'Name your product and set the quality for each of its 4 features.'}
              </Subtitle>
            </HeaderLeft>
            <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
          </HeaderBlock>

          <FormCard>
            <div>
              <FieldLabel>Product Name</FieldLabel>
              <TextInput
                type="text"
                value={name}
                onChange={this.onChangeName}
                placeholder="e.g. Hello"
                maxLength={NAME_MAX}
                invalid={nameInvalid}
                disabled={isSaving || editing}
                readOnly={editing}
              />
              {nameInvalid ? (
                <FieldError>Please enter a unique name (up to {NAME_MAX} letters or digits).</FieldError>
              ) : (
                <HelperText>
                  {editing
                    ? 'Product name cannot be changed once created.'
                    : `Up to ${NAME_MAX} letters or digits, no spaces or symbols.`}
                </HelperText>
              )}
            </div>

            <div>
              <FieldLabel>Features</FieldLabel>
              <FeatureHeaderRow>
                <div />
                <div />
                <FeatureColumnHeader>Quality Level</FeatureColumnHeader>
              </FeatureHeaderRow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {FEATURES.map(feature => (
                  <FeatureRow key={feature.key}>
                    <FeatureName>{feature.label}</FeatureName>
                    <FeatureSlider
                      min={RANK_MIN}
                      max={RANK_MAX}
                      value={ranks[feature.key]}
                      onChange={this.onChangeRank(feature.key)}
                      disabled={isSaving}
                    />
                    <RankBadge>{ranks[feature.key]}</RankBadge>
                  </FeatureRow>
                ))}
              </div>
              <HelperText>Each feature's quality can be set anywhere from {RANK_MIN} to {RANK_MAX}, with higher quality requiring more development time.</HelperText>
            </div>

            <FooterRow>
              <BtnSecondary onClick={onClose} disabled={isSaving}>Cancel</BtnSecondary>
              <BtnPrimary onClick={this.submit} disabled={isSaving}>
                <span>
                  {isSaving
                    ? (editing ? "Saving…" : "Creating…")
                    : (editing ? "Save Changes" : "Create Product")}
                </span>
                {!isSaving && <span>→</span>}
              </BtnPrimary>
            </FooterRow>
          </FormCard>
        </Page>
      </FinanceStyled.LenderOverlay>
    );
  }
}

export default AddProductPage;



// WEBPACK FOOTER //
// ./src/components/pd/decisions/add-product-page.js